import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';

/** Device image — same source as the original layout */
const deviceImage = 'https://tiefel.mehl-web.de/img/pv/stri2.png';

/** Format a decimal number with German comma, strip trailing zeros */
const g = (n, digits = 1) => n.toFixed(digits).replace('.', ',').replace(/0+$/, '').replace(/,$/, '');

/**
 * Convert raw { value, unit } to a more readable magnitude.
 *   h      →  Tage / Monate / „X Jahre Y Mon Z Tg"
 *   Wh     →  kWh / MWh / GWh
 *   kWh    →  MWh / GWh (if large enough)
 *   kW     →  MW / GW (if large enough)
 */
const formatLargeUnit = ({ value, unit }) => {
    if (!value || !unit) return { value: String(value || '–'), unit };

    const num = parseFloat(String(value).replace(',', '.'));
    if (isNaN(num)) return { value: String(value), unit };

    // ---- Time conversions ------------------------------------------
    if (unit === 'h') {
        if (num >= 8760) {                          // Jahre-Bereich → zusammengesetzt
            const jahre      = Math.floor(num / 8760);
            const restMonate = Math.floor((num % 8760) / 720);
            const tage       = Math.round(((num % 8760) % 720) / 24);
            let parts = [jahre + ' J'];
            if (restMonate > 0) parts.push(restMonate + ' Mon');
            if (tage > 0 && tage < 29) parts.push(tage + ' Tg');
            return { value: parts.join(', '), unit: '' };
        } else if (num >= 720) {                     // ~1 Monat
            const monate = num / 720;
            return { value: g(monate, 1), unit: 'Monate' };
        } else if (num >= 24) {
            const tage = num / 24;
            return { value: g(tage, 1), unit: 'Tage' };
        }
        return { value: String(value), unit };
    }

    // ---- Energy conversions ------------------------------------------
    if (unit === 'Wh') {
        if (num >= 1_000_000_000) {                   // GWh
            const gwh = num / 1_000_000_000;
            return { value: g(gwh, 2), unit: 'GWh' };
        }
        if (num >= 1_000_000) {                       // MWh
            const mwh = num / 1_000_000;
            return { value: g(mwh, 2), unit: 'MWh' };
        }
        if (num >= 1_000) {                           // kWh
            const kwh = num / 1_000;
            return { value: g(kwh, 1), unit: 'kWh' };
        }
        return { value: String(value), unit };
    }

    if (unit === 'kWh') {
        if (num >= 1_000_000) {                       // GWh
            const gwh = num / 1_000_000;
            return { value: g(gwh, 2), unit: 'GWh' };
        }
        if (num >= 10_000) {                          // MWh
            const mwh = num / 1_000;
            return { value: g(mwh, 2), unit: 'MWh' };
        }
        return { value: String(value), unit };
    }

    // ---- Power conversions -------------------------------------------
    if (unit === 'kW') {
        if (num >= 1_000_000) {                       // GW
            const gw = num / 1_000_000;
            return { value: g(gw, 3), unit: 'GW' };
        }
        if (num >= 100) {                             // MW
            const mw = num / 1_000;
            return { value: g(mw, 3), unit: 'MW' };
        }
        return { value: String(value), unit };
    }

    return { value: String(value), unit };
};

export const Wechselrichter = (props) => {
    let wr = props.value;
    let wr_data = wr.wr_data || [];

    // ---- KPI live data -------------------------------------------------
    const aktuelleLeistung  = wr_data.find(d => d.name === 'Aktuelle Leistung');
    const error             = wr_data.find(d => d.name === 'Fehler');
    const leistungsreduzierung = wr_data.find(d => d.name === 'Leistungsreduzierung');

    // ---- KPI cumulative ------------------------------------------------
    const gesamtertrag      = wr_data.find(d => d.name === 'Gesamtertrag');
    const betriebszeit      = wr_data.find(d => d.name === 'Betriebszeit');
    const einspeisezeit     = wr_data.find(d => d.name === 'Einspeisezeit');

    // ---- Parameter (not shown as KPI) ----------------------------------
    const kpiKeys = [
        'Aktuelle Leistung', 'Gesamtertrag', 'Betriebszeit',
        'Einspeisezeit', 'Fehler', 'Leistungsreduzierung'
    ];
    const details = wr_data.filter(d => !kpiKeys.includes(d.name));

    // ---- Status --------------------------------------------------------
    let statusClass  = 'status-ok';
    let statusText   = 'Betriebsbereit';
    if (error && String(error.value).match(/1|error|fehler/i)) {
        statusClass = 'status-error';
        statusText  = 'Fehler erkannt';
    } else if (leistungsreduzierung &&
               String(leistungsreduzierung.value).match(/1|true|ja/i)) {
        statusClass = 'status-warn';
        statusText  = 'Leistungsreduzierung';
    }

    // ---- Render a single data row helper -------------------------------
    const renderDataRow = (label, datum) => {
        const d = datum ? formatLargeUnit(datum) : null;
        return (
            <div className="wr-data-row">
                <span className="wr-data-label">{label}</span>
                <span className="wr-data-value">
                    {d ? d.value : '–'}
                    {d ? <sup>{d.unit}</sup> : ''}
                </span>
            </div>
        );
    };

    return (
        <Card className={`wechselrichter-card ${statusClass}`}>
            {/* ===== Header ===== */}
            <div className="wr-header">
                <div className="wr-header-left">
                    <h3 className="wr-title">{wr.name}</h3>
                    <span className={'wr-status-badge ' + statusClass}>{statusText}</span>
                </div>
            </div>

            <div className="wr-body">
                {/* ===== Device image + serial ===== */}
                <div className="wr-image-section">
                    <img src={deviceImage} alt={wr.name} className="wr-device-img" />
                    <div className="wr-serial-info">
                        <span className="wr-serial-label">Gerät</span>
                        <span className="wr-serial-value">{wr.key}</span>
                    </div>
                </div>

                {/* ===== Live data rows ===== */}
                <div className="wr-section">
                    <h5 className="wr-section-title">Live-Daten</h5>
                    {renderDataRow('Aktuelle Leistung', aktuelleLeistung)}
                    {leistungsreduzierung &&
                        renderDataRow('Leistungsreduzierung', leistungsreduzierung)
                    }
                    {error && renderDataRow('Fehlercode', error)}
                </div>

                {/* ===== Cumulative values (3-column, converted units) ===== */}
                <div className="wr-section">
                    <h5 className="wr-section-title">Kumuliert</h5>
                    {(() => {
                        const g = gesamtertrag  ? formatLargeUnit(gesamtertrag)  : null;
                        const b = betriebszeit  ? formatLargeUnit(betriebszeit)  : null;
                        const e = einspeisezeit ? formatLargeUnit(einspeisezeit) : null;
                        return (
                            <Row className="wr-cum-row">
                                <Col xs={4}>
                                    <div className="wr-cum-box wr-cum-green">
                                        <span className="wr-cum-value">{g ? g.value : '–'}</span>
                                        {g && <sup className="wr-cum-unit">{g.unit}</sup>}
                                        <span className="wr-cum-label">Gesamtertrag</span>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="wr-cum-box wr-cum-amber">
                                        <span className="wr-cum-value">{b ? b.value : '–'}</span>
                                        {b && <sup className="wr-cum-unit">{b.unit}</sup>}
                                        <span className="wr-cum-label">Betriebszeit</span>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="wr-cum-box wr-cum-teal">
                                        <span className="wr-cum-value">{e ? e.value : '–'}</span>
                                        {e && <sup className="wr-cum-unit">{e.unit}</sup>}
                                        <span className="wr-cum-label">Einspeisezeit</span>
                                    </div>
                                </Col>
                            </Row>
                        );
                    })()}
                </div>

                {/* ===== Parameter table ===== */}
                {details.length > 0 && (
                    <div className="wr-section">
                        <h5 className="wr-section-title">Parameter</h5>
                        <table className="wr-detail-table">
                            <tbody>
                                {details.map((d, i) => (
                                    <tr key={i}>
                                        <td className="wr-detail-label">{d.name}</td>
                                        <td className="wr-detail-value">
                                            {d.value}
                                            {d.unit ? ` ${d.unit}` : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Card>
    );
};
